import { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { CartContext } from '../contexts/CartContext';
import { useSearch } from '../contexts/SearchContext';
import { formatPrice } from '../utils/apiHelpers';
import WishlistIcon from './WishlistIcon';

function Header() {
  const { theme, changeTheme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();
  const { cart, itemCount, total, removeFromCart, loading: cartLoading } = useContext(CartContext);
  const { openSearch } = useSearch();
  const navigate = useNavigate();

  // Debug cart state
  if (process.env.NODE_ENV === 'development') {
    console.log('[Header] cart:', cart);
    console.log('[Header] itemCount:', itemCount, 'total:', total);
  }

  // Handle remove item from mini cart
  const handleRemoveFromCart = async (e, itemId) => {
    e.preventDefault();
    const result = await removeFromCart(itemId);
    if (result.success) {
      toast.success('Item removed from cart');
    } else {
      toast.error(result.error || 'Failed to remove item');
    }
  };

  const handleThemeChange = (e, newTheme) => {
    e.preventDefault();
    changeTheme(newTheme);
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    await logout();
    navigate('/login');
  };

  return (
    <>
      {/* START HEADER NAVIGATION */}
      <div className="header-area">
        <div className="container">
          <div className="row upper-nav">
            {/* Social Icons */}
            <div className="col-lg-3 col-md-3 d-none d-md-block nav-icon pt-3">
              <ul className="social-icons-simple text-left">
                <li>
                  <a href="/" onClick={(e) => e.preventDefault()} className="facebook-bg-hvr">
                    <i className="fab fa-facebook-f" aria-hidden="true"></i>
                  </a>
                </li>
                <li>
                  <a href="https://x.com/touchlinememory" target="_blank" rel="noreferrer" className="twitter-bg-hvr">
                    <i className="fab fa-twitter" aria-hidden="true"></i>
                  </a>
                </li>
                <li>
                  <a href="https://www.instagram.com/touchlinememories/" target="_blank" rel="noreferrer" className="instagram-bg-hvr">
                    <i className="fab fa-instagram" aria-hidden="true"></i>
                  </a>
                </li>
              </ul>
            </div>

            {/* Logo */}
            <div className="col-lg-6 col-md-6 col-6 text-center nav-logo pt-3">
              <Link to="/" className="navbar-brand">
                <img src="/assets/img/logo.jpg" alt="Touchline Memories" />
              </Link>
            </div>

            {/* Utility Icons - Search & Cart */}
            <div className="col-lg-3 col-md-3 col-4 nav-utility text-right d-flex justify-content-end align-items-center pt-3">
              <div className="manage-icons d-inline-block">
                <ul>
                  <li className="d-inline-block">
                    <a
                      id="add_search_box"
                      href="/"
                      onClick={(e) => {
                        e.preventDefault();
                        openSearch();
                      }}
                    >
                      <i className="fas fa-search search-sidebar-hover"></i>
                    </a>
                  </li>
                  <li className="d-inline-block">
                    <WishlistIcon />
                  </li>
                  <li className="d-inline-block mini-menu-card" id="mini-menu-card">
                    <a className="nav-link collapsePagesSideMenu" data-toggle="collapse" href="#sideNavPages4" style={{ position: 'relative' }}>
                      <i className="fas fa-shopping-cart"></i>
                      {itemCount > 0 && (
                        <span className="cart-count" style={{
                          position: 'absolute',
                          top: '-6px',
                          right: '-5px',
                          background: '#ff6b6b',
                          color: 'white',
                          borderRadius: '50%',
                          width: '18px',
                          height: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          lineHeight: '1'
                        }}>
                          {itemCount}
                        </span>
                      )}
                    </a>
                    <div id="sideNavPages4" className="collapse sideNavPages shopping-cart">
                      <i className="fas fa-caret-up mini-cart-caret"></i>
                      <div className="mini-cart-header text-left">
                        <h4>Shopping Cart ({itemCount} {itemCount === 1 ? 'item' : 'items'})</h4>
                      </div>
                      <div className="mini-cart-body">
                        <div className="inner-card">
                          {(!cart?.items || cart.items.length === 0) ? (
                            <div className="text-center py-3">
                              <p>Your cart is empty</p>
                              <Link to="/" className="btn btn-sm pink-gradient-btn-into-transparent">Shop Now</Link>
                            </div>
                          ) : (
                            cart.items.map((item) => (
                              <div className="media" key={item.id}>
                                <div className="img-holder ml-1 mr-2">
                                  <Link to={`/product-detail/${item.productId}`}>
                                    <img
                                      src={item.product?.images?.[0]?.url || '/assets/img/product-listing/p42.1.jpg'}
                                      className="align-self-center"
                                      alt={item.product?.name || 'Product'}
                                    />
                                  </Link>
                                </div>
                                <div className="media-body mt-auto mb-auto">
                                  <h5 className="name">
                                    <Link to={`/product-detail/${item.productId}`}>
                                      {item.product?.name || 'Product'}
                                    </Link>
                                  </h5>
                                  <p className="category">{item.product?.category?.name || item.product?.category || 'Art'}</p>
                                  <p className="price">
                                    <span>{formatPrice(item.price)}</span>(x{item.quantity}){' '}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleRemoveFromCart(e, item.id);
                                      }}
                                      disabled={cartLoading}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                      aria-label="Remove item"
                                    >
                                      <i className="fa fa-trash dustbin"></i>
                                    </button>
                                  </p>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        {itemCount > 0 && (
                          <div className="mini-cart-footer">
                            {/* Price breakdown */}
                            <div className="mini-cart-summary" style={{ fontSize: '12px', marginBottom: '10px', padding: '0 10px' }}>
                              <div className="d-flex justify-content-between">
                                <span>Subtotal:</span>
                                <span>{formatPrice(cart?.summary?.subtotal || 0)}</span>
                              </div>
                              {cart?.summary?.shipping > 0 && (
                                <div className="d-flex justify-content-between">
                                  <span>Shipping:</span>
                                  <span>{formatPrice(cart.summary.shipping)}</span>
                                </div>
                              )}
                              {cart?.summary?.hasFreeShipping && (
                                <div className="d-flex justify-content-between text-success">
                                  <span>Shipping:</span>
                                  <span>FREE</span>
                                </div>
                              )}
                              {cart?.summary?.tax > 0 && (
                                <div className="d-flex justify-content-between">
                                  <span>Tax (8%):</span>
                                  <span>{formatPrice(cart.summary.tax)}</span>
                                </div>
                              )}
                            </div>
                            <h5 style={{ borderTop: '1px solid #eee', paddingTop: '8px', paddingRight: '10px' }}>
                              Total: <span>{formatPrice(total)}</span>
                            </h5>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                              <Link to="/shop-cart" className="btn pink-gradient-btn-into-transparent" style={{ flex: '1', textAlign: 'center' }}>View Cart</Link>
                              <Link to="/checkout" className="btn pink-gradient-btn-into-transparent" style={{ flex: '1', textAlign: 'center' }}>Checkout</Link>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                  <li className="d-inline-block simple-dropdown" id="mini-menu">
                    <a className="nav-link collapsePagesSideMenu" data-toggle="collapse" href="#sideNavPages5">
                      <i className="fas fa-th"></i>
                    </a>
                    <div id="sideNavPages5" className="collapse sideNavPages user-utiliity">
                      <i className="fas fa-caret-up user-utiliity-caret"></i>
                      <h6 className="user-utiliity-title">MY ACCOUNT</h6>
                      <hr />
                      {isAuthenticated ? (
                        <ul className="text-left" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          <li className="user-greeting" style={{ display: 'block', padding: '5px 15px', fontWeight: '500' }}>
                            Hello, {user?.firstName || 'User'}
                          </li>
                          <li style={{ display: 'block', padding: '5px 15px' }}>
                            <Link to="/orders">My Orders</Link>
                          </li>
                          <li style={{ display: 'block', padding: '5px 15px' }}>
                            <a href="/" onClick={handleLogout}>Logout</a>
                          </li>
                        </ul>
                      ) : (
                        <ul className="text-left" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          <li style={{ display: 'block', padding: '5px 15px' }}>
                            <Link to="/register" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}>Register</Link>
                          </li>
                          <li style={{ display: 'block', padding: '5px 15px' }}>
                            <Link to="/login" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}>Login</Link>
                          </li>
                        </ul>
                      )}
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Main Navigation Menu */}
            <div className="col-lg-12 col-md-12 col-2 order-first order-md-last nav-mega">
              <header className="site-header" id="header">
                <nav className="navbar navbar-expand-md static-nav">
                  <div className="container position-relative megamenu-custom">
                    <Link className="navbar-brand center-brand" to="/">
                      <img src="/assets/img/logo.jpg" alt="logo" className="logo-scrolled" />
                    </Link>
                    <div className="collapse navbar-collapse">
                      <ul className="navbar-nav ml-auto mr-auto">
                        <li className="nav-item">
                          <Link className="nav-link" to="/">HOME</Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/product-listing">COLLECTIONS</Link>
                        </li>
                        <li className="nav-item dropdown static">
                          <a className="nav-link dropdown-toggle active" href="/" onClick={(e) => e.preventDefault()} data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            HALL OF FAME
                          </a>
                          <ul className="dropdown-menu megamenu flexable-megamenu">
                            <li>
                              <div className="container">
                                <div className="row">
                                  <div className="col-lg-3 col-md-6 col-sm-12 mengmenu_border">
                                    <h5 className="dropdown-title">Tournaments</h5>
                                    <ul>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=champions-league">Best of Europe</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=premier-league">Made in England</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=fa-cup">The Big Cup</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=world-cup">Best of the World</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=africa-cup-of-nations">Best of Africa</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=womens-world-cup">Best of the World (Women)</Link></li>
                                    </ul>
                                    <h5 className="dropdown-title">Best of Europe</h5>
                                    <ul>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=finals">Finals</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=semi-finals">Semi Finals</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=hat-tricks">Hat Tricks</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=one-twos">One-Twos</Link></li>
                                    </ul>
                                  </div>
                                  <div className="col-lg-3 col-md-6 col-sm-12 mengmenu_border">
                                    <h5 className="dropdown-title">22 Best of the World</h5>
                                    <ul>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=finals">Finals</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=semi-finals">Semi Finals</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=free-kicks">Free kicks</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=surprise-goals">Surprise Goals</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=hat-tricks">Hat Tricks</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=one-twos">One-Twos</Link></li>
                                    </ul>
                                  </div>
                                  <div className="col-lg-6 col-md-12 col-sm-12">
                                    <h5 className="dropdown-title text-left">Featured Art</h5>
                                    <div className="carousel-menu mt-4">
                                      <div className="featured-megamenu-carousel owl-carousel owl-theme">
                                        <div className="item">
                                          <img src="/assets/img/shop1.jpg" alt="shop-image" />
                                        </div>
                                        <div className="item">
                                          <img src="/assets/img/shop2.jpg" alt="shop-image" />
                                        </div>
                                      </div>
                                      <i className="lni-chevron-left ini-customPrevBtn"></i>
                                      <i className="lni-chevron-right ini-customNextBtn"></i>
                                    </div>
                                    <p className="mt-4 megamenu-slider-para">
                                      She is widely regarded as one of the greatest strikers of all time and one of the best female footballers in the world.
                                    </p>
                                    <Link to="/product-listing" className="btn trans-pink-color-gradient-btn slider-btn text-left">BUY ART</Link>
                                  </div>
                                </div>
                              </div>
                            </li>
                          </ul>
                        </li>
                        <li className="nav-item dropdown static">
                          <a className="nav-link dropdown-toggle" href="/" onClick={(e) => e.preventDefault()} data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            FOOTBALL
                          </a>
                          <ul className="dropdown-menu megamenu flexable-megamenu">
                            <li>
                              <div className="container">
                                <div className="row">
                                  <div className="col-lg-4 col-md-6 col-sm-12 mengmenu_border">
                                    <h5 className="dropdown-title">Tournaments</h5>
                                    <ul>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=champions-league">Best of Europe</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=premier-league">Made in England</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=fa-cup">The Big Cup</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=world-cup">Best of the World</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=africa-cup-of-nations">Best of Africa</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=womens-world-cup">Best of the World (Women)</Link></li>
                                    </ul>
                                  </div>
                                  <div className="col-lg-4 col-md-6 col-sm-12 mengmenu_border">
                                    <h5 className="dropdown-title opacity-10">Collections</h5>
                                    <ul>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=finals">Finals</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=semi-finals">Semi Finals</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=free-kicks">Free kicks</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=surprise-goals">Surprise Goals</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=hat-tricks">Hat Tricks</Link></li>
                                      <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/products?tag=one-twos">One-Twos</Link></li>
                                    </ul>
                                  </div>
                                  <div className="col-lg-4 col-md-6 col-sm-12">
                                    <h5 className="dropdown-title bottom10">22 Best of the World</h5>

                                    <div className="media outlet-media mt-3">
                                      <div className="box">
                                        <img className="align-self-start" src="/assets/img/placeholder.svg" alt="Best of the World" />
                                      </div>
                                      <div className="media-body">
                                        <h6 className="mt-3 ml-3"><Link to="/product-listing">Best of the World</Link></h6>
                                      </div>
                                    </div>

                                  </div>
                                </div>
                              </div>
                            </li>
                          </ul>
                        </li>
                        <li className="nav-item dropdown position-relative">
                          <a className="nav-link dropdown-toggle" href="/" onClick={(e) => e.preventDefault()} data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            OTHER SPORTS
                          </a>
                          <div className="dropdown-menu">
                            <ul>
                              <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/category/baseball">Baseball</Link></li>
                              <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/category/american-football">American Football</Link></li>
                              <li><i className="lni-angle-double-right right-arrow"></i><Link className="dropdown-item" to="/category/basketball">Basketball</Link></li>
                            </ul>
                          </div>
                        </li>
                        <li className="nav-item dropdown position-relative theme-switcher-menu">
                          <a className="nav-link dropdown-toggle" href="/" onClick={(e) => e.preventDefault()} data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" aria-label="Switch Color Mode">
                            DISPLAY OPTIONS
                          </a>
                          <div className="dropdown-menu">
                            <ul>
                              <li>
                                <i className="lni-angle-double-right right-arrow"></i>
                                <a
                                  className={`dropdown-item theme-option ${theme === 'light' ? 'active' : ''}`}
                                  href="/"
                                  onClick={(e) => handleThemeChange(e, 'light')}
                                  role="button"
                                  aria-label="Switch to Light mode"
                                >
                                  Default (Light)
                                </a>
                              </li>
                              <li>
                                <i className="lni-angle-double-right right-arrow"></i>
                                <a
                                  className={`dropdown-item theme-option ${theme === 'dark' ? 'active' : ''}`}
                                  href="/"
                                  onClick={(e) => handleThemeChange(e, 'dark')}
                                  role="button"
                                  aria-label="Switch to Dark mode"
                                >
                                  Dark Mode
                                </a>
                              </li>
                              <li>
                                <i className="lni-angle-double-right right-arrow"></i>
                                <a
                                  className={`dropdown-item theme-option ${theme === 'yellow' ? 'active' : ''}`}
                                  href="/"
                                  onClick={(e) => handleThemeChange(e, 'yellow')}
                                  role="button"
                                  aria-label="Switch to Bright Yellow mode"
                                >
                                  Bright Yellow
                                </a>
                              </li>
                            </ul>
                          </div>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/contact">CONTACT</Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/faq">FAQ</Link>
                        </li>
                      </ul>
                    </div>
                    {/* Side menu open button */}
                    <a href="/" onClick={(e) => e.preventDefault()} className="d-inline-block sidemenu_btn d-lg-none d-md-block" id="sidemenu_toggle">
                      <span></span> <span></span> <span></span>
                    </a>
                  </div>
                </nav>
                </header>
              </div>
            </div>
          </div>
        </div>
      {/* END HEADER NAVIGATION */}
    </>
  );
}

export default Header;
